import { useCallback, useEffect, useRef } from "react"
import { create } from "zustand"

import { OIO_STAGES } from "@/features/oio/config/oio.config"
import { useGenerationStore } from "@/features/oio/generation.store"
import { useOrderDraftStore } from "@/features/order-draft/store/order-draft.store"
import { useAnalysisStore } from "@/features/analysis/store/analysis.store"
import { useReviewStore } from "@/features/oio/store/review.store"
import type { OIOAgentState, UseOIOAgentResult } from "@/features/oio/hooks/useOIOAgent"
import { MOCK_THREAD_ID, shortCaseLabel } from "./mockMode"
import type { MockOioFixture } from "./fixture"

/**
 * MOCK REPLAY ENGINE — replays a recorded real run through the exact agent
 * state shape the workspace consumes (OIOAgentState), on a scripted timer.
 *
 * Everything is deterministic: the timeline below is derived from OIO_STAGES
 * and fixed per-stage durations (no Math.random anywhere), so every replay of
 * the same fixture at the same speed produces the same sequence of states.
 *
 * State lives in a module-level zustand store so the three independent
 * consumers stay in sync without prop-drilling:
 *   - useMockOIOAgent()  — the drop-in replacement for useOIOAgent's state
 *   - useMockRun()       — the controls (banner chip, seeded uploader)
 *   - the timer engine   — module-level functions below
 *
 * NOTE: this module must NOT import the fixture JSON (see fixture.ts) — the
 * real path imports this file statically via useOIOAgent, and the fixture
 * should only travel in the lazily-loaded mock chunk. The fixture is passed
 * INTO startMockRun() by the mock components instead.
 */

// ── Timeline ───────────────────────────────────────────────────────────────

/**
 * Officer-watchable pacing, ~75s total at 1x (exactly 73s):
 *   ingest 6s → alignment 6s (aligned allegations land) → analysis 20s
 *   (substages rotate) → speaking_order 8s (findings/stance land — the money
 *   moment) → deliberation 15s → section_draft 12s → compile 6s → completed
 *   (draft + review report + artifact land).
 */
export const MOCK_STAGE_DURATIONS_S: Record<string, number> = {
  ingest: 6,
  alignment: 6,
  analysis: 20,
  speaking_order: 8,
  deliberation: 15,
  section_draft: 12,
  compile: 6,
}

export const MOCK_TOTAL_DURATION_S = OIO_STAGES.reduce(
  (sum, s) => sum + (MOCK_STAGE_DURATIONS_S[s.id] ?? 6),
  0,
)

export interface MockTimelineStep {
  /** Seconds from start() at 1x speed. */
  at: number
  patch: Partial<OIOAgentState>
}

/** What lands when a stage COMPLETES — mirrors the real node-boundary flushes. */
function stageLandings(stageId: string, fx: MockOioFixture): Partial<OIOAgentState> {
  switch (stageId) {
    case "ingest":
      // reply_status is recorded at ingest on the real pipeline.
      return { reply_status: fx.reply_status }
    case "alignment":
      return { aligned_allegations_json: fx.aligned_allegations_json }
    case "speaking_order":
      return { findings_summary_json: fx.findings_summary_json }
    case "compile":
      // "draft_completed" is the exact literal the workspace's summary card,
      // useCaseDraftStatus and the rewrite switch key on.
      return {
        draft_status: "draft_completed",
        current_stage: null,
        current_substage: null,
        oio_draft: fx.oio_draft,
        review_report_json: fx.review_report_json,
        artifact_blob_url: fx.artifact_blob_url,
        draft_revision: fx.draft_revision,
      }
    default:
      return {}
  }
}

export function buildMockTimeline(fx: MockOioFixture): MockTimelineStep[] {
  const steps: MockTimelineStep[] = []
  let t = 0
  const completed: string[] = []
  for (const stage of OIO_STAGES) {
    const duration = MOCK_STAGE_DURATIONS_S[stage.id] ?? 6
    const substages = stage.substages?.length ? stage.substages : [null]
    const slice = duration / substages.length
    substages.forEach((sub, i) => {
      steps.push({
        at: t + i * slice,
        patch: { current_stage: stage.id, current_substage: sub },
      })
    })
    t += duration
    completed.push(stage.id)
    steps.push({
      at: t,
      patch: { completed_stages: [...completed], ...stageLandings(stage.id, fx) },
    })
  }
  return steps
}

// ── Store ──────────────────────────────────────────────────────────────────

const IDLE_AGENT: OIOAgentState = {
  thread_id: MOCK_THREAD_ID,
  draft_status: "draft_not_started",
  completed_stages: [],
  current_stage: null,
  current_substage: null,
  oio_draft: null,
  artifact_blob_url: null,
  draft_revision: null,
  findings_summary_json: null,
  aligned_allegations_json: null,
  review_report_json: null,
  reply_status: null,
}

interface MockRunStoreState {
  agent: OIOAgentState
  running: boolean
  /** Playback speed multiplier — 1x (officer-watchable) or 4x (quick check). */
  speed: number
}

export const useMockRunStore = create<MockRunStoreState>(() => ({
  agent: IDLE_AGENT,
  running: false,
  speed: 1,
}))

// ── Engine (module-level; timers owned by the single useMockRun mount) ─────

let timer: ReturnType<typeof setTimeout> | null = null
let timeline: MockTimelineStep[] = []
let stepIndex = 0
let lastAt = 0
let runTitle = "Mock replay"

function stopMockTimers(): void {
  if (timer !== null) {
    clearTimeout(timer)
    timer = null
  }
}

/**
 * The workspace persists per-case artifacts under these localStorage keys.
 * Clearing them for the mock case id keeps every replay deterministic (and
 * keeps mock leftovers from accumulating).
 */
function clearMockCaseCaches(): void {
  try {
    for (const prefix of ["oio-findings-", "oio-allegs-", "oio-review-", "oio-draft-"]) {
      window.localStorage.removeItem(prefix + MOCK_THREAD_ID)
    }
  } catch {
    /* storage unavailable — nothing cached */
  }
}

function resetDependentStores(): void {
  useOrderDraftStore.getState().reset()
  useAnalysisStore.getState().reset()
  useReviewStore.getState().reset()
}

function finishRun(): void {
  stopMockTimers()
  useMockRunStore.setState({ running: false })
  const { agent } = useMockRunStore.getState()
  useGenerationStore
    .getState()
    .setStatus(
      MOCK_THREAD_ID,
      agent.draft_status === "draft_completed" ? "completed" : "failed",
      runTitle,
    )
}

function advance(): void {
  if (stepIndex >= timeline.length) {
    finishRun()
    return
  }
  // Apply every step scheduled for the same instant in one state update
  // (e.g. "stage N completed" + "stage N+1 started" share a timestamp).
  const at = timeline[stepIndex].at
  lastAt = at
  let patch: Partial<OIOAgentState> = {}
  while (stepIndex < timeline.length && timeline[stepIndex].at === at) {
    patch = { ...patch, ...timeline[stepIndex].patch }
    stepIndex++
  }
  const agent = { ...useMockRunStore.getState().agent, ...patch }
  useMockRunStore.setState({ agent })

  // Mirror progress into the per-case generation store — exactly what
  // GenerationTracker does for real runs (it is inert in mock mode). This is
  // what drives useLiveThoughts' transcript and keeps the progress checklist
  // stable across tab switches.
  useGenerationStore.getState().setProgress(MOCK_THREAD_ID, {
    completedStages: agent.completed_stages,
    currentStage: agent.current_stage,
    currentSubstage: agent.current_substage,
  })

  if (agent.draft_status === "draft_completed" || stepIndex >= timeline.length) {
    finishRun()
    return
  }
  const delayMs = ((timeline[stepIndex].at - at) * 1000) / useMockRunStore.getState().speed
  timer = setTimeout(advance, delayMs)
}

/** Begin (or restart) the scripted replay of a fixture. */
export function startMockRun(fixture: MockOioFixture): void {
  stopMockTimers()
  clearMockCaseCaches()
  resetDependentStores()
  timeline = buildMockTimeline(fixture)
  stepIndex = 0
  lastAt = 0
  runTitle = shortCaseLabel(fixture.case_label)
  useMockRunStore.setState({
    running: true,
    agent: { ...IDLE_AGENT, draft_status: "draft_running" },
  })
  useGenerationStore.getState().begin(MOCK_THREAD_ID, runTitle)
  advance() // applies the t=0 steps synchronously, then self-schedules
}

/** Stop the replay and return everything to the pre-run (seeded uploader) state. */
export function resetMockRun(): void {
  stopMockTimers()
  timeline = []
  stepIndex = 0
  lastAt = 0
  clearMockCaseCaches()
  resetDependentStores()
  useGenerationStore.getState().remove(MOCK_THREAD_ID)
  useMockRunStore.setState({ agent: IDLE_AGENT, running: false })
}

/**
 * Change playback speed (1x / 4x). Simplification, documented: the pending
 * step's FULL inter-step delay is re-run at the new speed — deterministic,
 * and at most a few seconds of drift on a demo clock.
 */
export function setMockSpeed(speed: number): void {
  const prev = useMockRunStore.getState().speed
  useMockRunStore.setState({ speed })
  if (prev === speed || timer === null) return
  stopMockTimers()
  if (stepIndex < timeline.length) {
    const delayMs = ((timeline[stepIndex].at - lastAt) * 1000) / speed
    timer = setTimeout(advance, delayMs)
  }
}

/** Unmount cleanup: kill timers and drop the mock run from the generation store. */
export function teardownMockRun(): void {
  stopMockTimers()
  useMockRunStore.setState({ running: false })
  useGenerationStore.getState().remove(MOCK_THREAD_ID)
}

// ── Hooks ──────────────────────────────────────────────────────────────────

export interface MockRunControls {
  /** The exact OIOAgentState shape the workspace consumes. */
  state: OIOAgentState
  running: boolean
  speed: number
  start: () => void
  reset: () => void
  setSpeed: (speed: number) => void
}

/**
 * Controls + live state for the scripted replay. Mount ONCE (MockDraftingRoute
 * owns it): the unmount cleanup stops the timers, so a second mount would tie
 * the replay's lifetime to whichever unmounts first.
 */
export function useMockRun(fixture: MockOioFixture): MockRunControls {
  const agent = useMockRunStore((s) => s.agent)
  const running = useMockRunStore((s) => s.running)
  const speed = useMockRunStore((s) => s.speed)

  const fixtureRef = useRef(fixture)
  fixtureRef.current = fixture

  const start = useCallback(() => startMockRun(fixtureRef.current), [])
  const reset = useCallback(() => resetMockRun(), [])
  const setSpeed = useCallback((s: number) => setMockSpeed(s), [])

  // Timers are cleaned up when the owning component unmounts.
  useEffect(() => () => teardownMockRun(), [])

  return { state: agent, running, speed, start, reset, setSpeed }
}

/**
 * Drop-in replacement for useOIOAgent when mock replay is active — same
 * return contract, fed from the replay store. run() is a no-op (nothing to
 * stream), setState patches the mock state (useRewriteCompletion and the
 * workspace's thread_id injection go through here harmlessly).
 */
export function useMockOIOAgent(): UseOIOAgentResult {
  const state = useMockRunStore((s) => s.agent)
  const running = useMockRunStore((s) => s.running)

  const setState = useCallback(
    (updater: OIOAgentState | ((prev: OIOAgentState | undefined) => OIOAgentState)) => {
      useMockRunStore.setState((prev) => ({
        agent: typeof updater === "function" ? updater(prev.agent) : updater,
      }))
    },
    [],
  )
  const run = useCallback(async () => {}, [])

  return { state, setState, run, running }
}
